import {
  GridFilterInputValue,
  GridFilterItem,
  GridFilterOperator,
  getGridSingleSelectOperators
} from '@mui/x-data-grid-pro';
import { differenceInCalendarDays, isValid, parse, parseISO } from 'date-fns';

type DaysFromToday = (daysFromToday: number) => boolean;
export type DayReader = (value: unknown) => Date;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}(?=$|[T\s])/;
const US_DAY = /^(\d{1,2}\/\d{1,2}\/\d{4})(?:\s+\d{1,2}:\d{2}\b.*)?$/;

// Stricter than getDate, whose new Date fallback reads text like "Term 3" as a date.
const readDay = (value: unknown, readIso: (iso: string) => Date): Date => {
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return new Date(NaN);
  const text = value.trim();
  if (ISO_DAY.test(text)) return readIso(text);
  const usDay = US_DAY.exec(text)?.[1];
  return usDay ? parse(usDay, 'M/d/yyyy', new Date()) : new Date(NaN);
};

// For a timestamp whose cell shows it in the user's own time.
export const localDay: DayReader = (value) => readDay(value, parseISO);

// For a calendar date whose cell shows the day as stored, even when a time was stored with it.
export const writtenDay: DayReader = (value) => readDay(value, (iso) => parseISO(iso.slice(0, 10)));

const wholeDays = (value: unknown): number | null => {
  const days = value === '' || value == null ? NaN : Number(value);
  return Number.isInteger(days) && days >= 1 ? days : null;
};

const keepingDays = (read: DayReader, keeps: DaysFromToday) => {
  // Read when MUI builds the filter, not per row: a tab left open past midnight keeps yesterday until a refilter.
  const today = new Date();
  return (value: unknown): boolean => {
    const date = read(value);
    return isValid(date) && keeps(differenceInCalendarDays(date, today));
  };
};

const todayOperator = (value: string, label: string, keeps: DaysFromToday, read: DayReader): GridFilterOperator => ({
  value,
  label,
  requiresFilterValue: false,
  getApplyFilterFn: () => keepingDays(read, keeps)
});

const withinOperator = (
  value: string,
  label: string,
  keeps: (days: number) => DaysFromToday,
  read: DayReader
): GridFilterOperator => ({
  value,
  label,
  InputComponent: GridFilterInputValue,
  InputComponentProps: { type: 'number', InputProps: { inputProps: { min: 1, step: 1 } } },
  getApplyFilterFn: (item: GridFilterItem) => {
    const days = wholeDays(item.value);
    return days === null ? null : keepingDays(read, keeps(days));
  }
});

export const relativeDateOperators = (read: DayReader = localDay): GridFilterOperator[] => [
  todayOperator('isToday', 'is today', (days) => days === 0, read),
  todayOperator('beforeToday', 'before today', (days) => days < 0, read),
  todayOperator('onOrBeforeToday', 'on or before today', (days) => days <= 0, read),
  todayOperator('afterToday', 'after today', (days) => days > 0, read),
  todayOperator('onOrAfterToday', 'on or after today', (days) => days >= 0, read),
  withinOperator('withinLastDays', 'within the last N days', (n) => (days) => days <= 0 && days >= -n, read),
  withinOperator('withinNextDays', 'within the next N days', (n) => (days) => days >= 0 && days <= n, read)
];

export const singleSelectDateOperators = (): GridFilterOperator[] => [
  ...getGridSingleSelectOperators(),
  ...relativeDateOperators()
];
